import React, { useEffect, useState } from 'react';
import './VideoComments.css';

const VideoComments = ({ videoId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedComments, setExpandedComments] = useState(new Set());

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const API_LIST = [
          'AIzaSyAM9R1vr9t6GSrazqGtw5t8RBKmz2Q17dk',
          'AIzaSyCvaktMcshCgWbahXn7JuJcmhUlSR5ZxAA',
          'AIzaSyBboFo9umdEW03mwz8IdLsRVWXEeAyokdw',
          'AIzaSyAmw-1dYBqj1MIqccmtfeLWfgzYqROkylY',
          'AIzaSyBXaSHnkg1JHTCn8um-q4AfwlyDrC0g5ng',
          'AIzaSyA-xoSvUcnAYUK2v6sTeJtQT4NG9JxWrQg',
          'AIzaSyDwCaQVTwJsau514qhaWTMBlZ80iQgKLtM',
          'AIzaSyB-9_ZEDWXvVl5_Mc9PQ3FTUxgHMA5qCs4',
          'AIzaSyBL-y6KSyd1roD9rAY6tzRuJpiSRBVxww8',
          'AIzaSyCfscBsyvhAIjMzb8JSr95Ub2bgNvnYwZc',
        ];

        const randomIndex = Math.floor(Math.random() * API_LIST.length);
        const API_KEY = API_LIST[randomIndex];

        const apiUrl = `https://www.googleapis.com/youtube/v3/commentThreads?part=snippet&videoId=${videoId}&key=${API_KEY}`;

        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error('Failed to fetch comments');
        }

        const data = await response.json();

        const commentsData = data.items.map(item => {
          const commentSnippet = item.snippet.topLevelComment.snippet;
          return {
            id: item.id,
            commenterName: commentSnippet.authorDisplayName,
            comment: commentSnippet.textDisplay,
            likeCount: item.snippet.topLevelComment.snippet.likeCount,
            publishedAt: commentSnippet.publishedAt,
            replies: item.snippet.totalReplyCount,
            authorProfileImageUrl: commentSnippet.authorProfileImageUrl,
          };
        });

        setComments(commentsData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching comments:', error);
        setLoading(false);
      }
    };

    fetchComments();
  }, [videoId]);

  const toggleCommentExpansion = (commentId) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId);
    } else {
      newExpanded.add(commentId);
    }
    setExpandedComments(newExpanded);
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const commentDate = new Date(dateString);
    const diffInSeconds = Math.floor((now - commentDate) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
    return `${Math.floor(diffInSeconds / 31536000)}y ago`;
  };

  const formatLikeCount = (count) => {
    if (count === 0) return '';
    if (count < 1000) return count.toString();
    if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
    return `${(count / 1000000).toFixed(1)}M`;
  };

  const truncateComment = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (comments.length === 0 && !loading) {
    return <></>;
  }

  return (
    <div className="video-comments-container">
      <div className="comments-header">
        <div className="comments-title">
          <h2>💬 Comments</h2>
          {!loading && (
            <span className="comments-count">
              {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
            </span>
          )}
        </div>
        <div className="comments-divider"></div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <p className="loading-text">Loading comments...</p>
        </div>
      ) : (
        <div className="comments-list">
          {comments.map((comment, index) => {
            const isExpanded = expandedComments.has(comment.id);
            const shouldShowReadMore = comment.comment.length > 200;
            const displayText = isExpanded ? comment.comment : truncateComment(comment.comment);

            return (
              <div 
                key={comment.id} 
                className="comment-item"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="comment-avatar">
                  <img
                    src={comment.authorProfileImageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.commenterName)}&background=random&color=fff&size=40`}
                    alt={`${comment.commenterName}'s avatar`}
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.commenterName)}&background=random&color=fff&size=40`;
                    }}
                  />
                </div>

                <div className="comment-content">
                  <div className="comment-header">
                    <span className="commenter-name">{comment.commenterName}</span>
                    <span className="comment-time">{formatTimeAgo(comment.publishedAt)}</span>
                  </div>

                  <div className="comment-text">
                    <p dangerouslySetInnerHTML={{ __html: displayText }} />
                    {shouldShowReadMore && (
                      <button
                        className="read-more-btn"
                        onClick={() => toggleCommentExpansion(comment.id)}
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>

                  <div className="comment-actions">
                    <div className="comment-stats">
                      {comment.likeCount > 0 && (
                        <span className="like-count">
                          <span className="like-icon">👍</span>
                          {formatLikeCount(comment.likeCount)}
                        </span>
                      )}
                      {comment.replies > 0 && (
                        <span className="reply-count">
                          <span className="reply-icon">💬</span>
                          {comment.replies} {comment.replies === 1 ? 'reply' : 'replies'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style jsx>{`
        .video-comments-container {
          max-width: 100%;
          margin: 2rem 0;
          padding: 0 1rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        }

        .comments-header {
          margin-bottom: 2rem;
        }

        .comments-title {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .comments-title h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a1a;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .comments-count {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .comments-divider {
          height: 3px;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
          border-radius: 2px;
          width: 100%;
          margin-top: 0.5rem;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 1rem;
          text-align: center;
        }

        .loading-spinner {
          margin-bottom: 1rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-text {
          color: #666;
          font-size: 1rem;
          margin: 0;
        }

        .comments-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .comment-item {
          display: flex;
          gap: 1rem;
          padding: 1.5rem;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          animation: slideInUp 0.6s ease-out both;
          position: relative;
          overflow: hidden;
        }

        .comment-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .comment-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .comment-item:hover::before {
          transform: scaleX(1);
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .comment-avatar {
          flex-shrink: 0;
        }

        .comment-avatar img {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid #f0f0f0;
          transition: border-color 0.3s ease;
        }

        .comment-item:hover .comment-avatar img {
          border-color: #667eea;
        }

        .comment-content {
          flex: 1;
          min-width: 0;
        }

        .comment-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .commenter-name {
          font-weight: 600;
          color: #1a1a1a;
          font-size: 0.95rem;
        }

        .comment-time {
          color: #666;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .comment-text {
          margin-bottom: 1rem;
          line-height: 1.6;
        }

        .comment-text p {
          margin: 0;
          color: #333;
          font-size: 0.95rem;
          word-wrap: break-word;
        }

        .read-more-btn {
          background: none;
          border: none;
          color: #667eea;
          font-weight: 600;
          cursor: pointer;
          padding: 0.25rem 0;
          margin-top: 0.5rem;
          font-size: 0.875rem;
          transition: color 0.3s ease;
        }

        .read-more-btn:hover {
          color: #764ba2;
          text-decoration: underline;
        }

        .comment-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .comment-stats {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .like-count, .reply-count {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #666;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .like-icon, .reply-icon {
          font-size: 1rem;
          opacity: 0.8;
        }

        .like-count:hover .like-icon {
          transform: scale(1.2);
          transition: transform 0.2s ease;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .video-comments-container {
            padding: 0 0.5rem;
          }

          .comment-item {
            padding: 1rem;
            gap: 0.75rem;
          }

          .comment-avatar img {
            width: 32px;
            height: 32px;
          }

          .comments-title {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .comments-title h2 {
            font-size: 1.25rem;
          }

          .comments-count {
            font-size: 0.8rem;
            padding: 0.2rem 0.6rem;
          }

          .comment-stats {
            gap: 1rem;
          }
        }

        @media (max-width: 480px) {
          .comment-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }

          .comment-stats {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default VideoComments;